import React from 'react';
import styled, { css } from 'styled-components';
import SVG from 'react-inlinesvg';
import { isDarkThemeMixin, Switch, TextButton } from '@amzn/storm-ui';
import { useSelector, useDispatch } from 'react-redux';
import { selectEmbeddedChatOpen, setEmbeddedChatOpen } from 'src/app/slice';
import { setEmbeddedChatVisible } from 'src/commons/utils/a3cEmbeddedChat';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { HiddenContent } from './styles';

export const ADS_AGENT_OPEN_ICON_PATH =
  'https://m.media-amazon.com/images/G/01/CCX/svg/A3C_nav_button_Open.svg';
export const ADS_AGENT_CLOSE_ICON_PATH =
  'https://m.media-amazon.com/images/G/01/CCX/svg/A3C_nav_button_Closed.svg';

export const AdsAgentButtonWrapper = styled.div<{ isNavOpen: boolean }>`
  min-height: 40px;
  width: 296px;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

export const AdsAgentDivider = styled.hr<{ isNavOpen: boolean }>`
  border: none;
  border-top: 2px solid ${({ theme }) => theme.border.color.subtle.default};
  margin: 10px 0px;
  margin-inline: 4px;
  width: ${({ isNavOpen }) => (isNavOpen ? 'calc(100% - 8px)' : '32px')};
`;

export const AdsAgentIconWrapper = styled.div<{ isOpen: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-inline-end: ${({ theme }) => theme.spacing.small.default};

  & svg path {
    ${isDarkThemeMixin(css<{ isOpen: boolean }>`
      fill: ${({ isOpen }) => (isOpen ? '#B07CFF' : '#F9F8F6')};
    `)};
  }
`;

export const StyledSwitchWrapper = styled.div`
  position: absolute;
  inset-inline-end: ${({ theme }) => theme.spacing.xsmall.default};
  top: 20px;
  transform: translateY(-50%);
  pointer-events: none;
`;

export const AdsAgentButtonInner = styled(TextButton)<{
  isNavOpen: boolean;
}>`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  border: none;
  background-color: ${({ theme }) =>
    theme.selectableContainer.backgroundColor.default};
  min-height: 40px;
  padding-inline: ${({ theme }) =>
    `${theme.spacing.xxsmall.default} ${theme.spacing.xsmall.default}`};
  border-radius: ${({ theme }) => theme.border.radius.large};
  color: ${({ theme }) => theme.text.color.default};
  font: inherit;

  &&& {
    text-decoration: none;
    color: ${({ theme }) => theme.text.color.default};
  }
`;

interface AdsAgentNavButtonProps {
  isNavOpen: boolean;
}

const AdsAgentNavButton: React.VFC<AdsAgentNavButtonProps> = ({
  isNavOpen,
}) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectEmbeddedChatOpen);
  const bundle = useSidenavBundle();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};

  const handleClick = () => {
    const nextState = !isOpen;
    dispatch(setEmbeddedChatOpen(nextState));
    setEmbeddedChatVisible(nextState);
  };

  const iconPath = isOpen
    ? ADS_AGENT_OPEN_ICON_PATH
    : ADS_AGENT_CLOSE_ICON_PATH;

  const label = bundle.getMessage('sidenav-ads-agent-label');
  const switchLabel = bundle.getMessage('sidenav-ads-agent-switch-label');

  return (
    <AdsAgentButtonWrapper isNavOpen={isNavOpen}>
      <AdsAgentButtonInner
        isNavOpen={isNavOpen}
        data-ccx-e2e-id="ads-agent-nav-button"
        onClick={handleClick}
        aria-label={label}
        aria-pressed={isOpen}
      >
        <AdsAgentIconWrapper isOpen={isOpen}>
          <SVG src={iconPath} width={24} height={24} />
        </AdsAgentIconWrapper>
        <HiddenContent alignItems="center" justifyContent="space-between">
          {label}
        </HiddenContent>
      </AdsAgentButtonInner>
      <StyledSwitchWrapper
        aria-hidden="true"
        ref={(el: HTMLDivElement | null) => {
          if (el) (el as any).inert = true;
        }}
      >
        <Switch
          id="ads-agent-toggle"
          toggled={isOpen}
          onChange={noop}
          small
          switchLabel={switchLabel}
        />
      </StyledSwitchWrapper>
      <AdsAgentDivider isNavOpen={isNavOpen} role="separator" />
    </AdsAgentButtonWrapper>
  );
};

export default AdsAgentNavButton;
